import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { Search, CircleSlash, CheckCircle2, Loader2, X } from "lucide-react";

/** Video Library Curation Part 2/3 (2026-08) — small project-picker popover shared by a single
 * card's "Link" action (multi-select) and the bulk-select action bar (single-select: bulk-link
 * targets exactly ONE project at a time, per spec). Deliberately its own tiny component, not a
 * copy of GlobalAddVideoModal (that's a full Add-Video flow with a URL input; this is just
 * "pick project(s), submit"). */
export const ProjectPicker = ({ multiple = true, onSubmit, onClose, submitLabel = "Link" }) => {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/v1/projects/").then((r) => setProjects(r.data.items || [])).catch(() => setProjects([]));
  }, []);

  const filtered = useMemo(() => {
    const ql = query.toLowerCase().trim();
    return projects.filter((p) => !ql || (p.name || "").toLowerCase().includes(ql) || (p.client_name || "").toLowerCase().includes(ql));
  }, [projects, query]);

  const toggle = (id) => setSelectedIds((prev) => (multiple ? (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]) : [id]));

  const submit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    await onSubmit(multiple ? selectedIds : selectedIds[0]);
    setSubmitting(false);
  };

  return (
    <div data-testid="project-picker-popover" className="absolute z-20 mt-1 w-64 rounded-md border border-hairline bg-paper shadow-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-faint">{multiple ? "Link to Project(s)" : "Link to Project"}</span>
        <button onClick={onClose} data-testid="project-picker-close-btn" className="text-faint hover:text-ink"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-faint" />
        <input data-testid="project-picker-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search projects…"
          className="w-full rounded-md border border-hairline bg-paper pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:border-brand/50" />
      </div>
      <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-hairline">
        {filtered.length === 0 ? (
          <div className="px-1 py-3 text-[11px] text-faint inline-flex items-center gap-1.5"><CircleSlash className="h-3 w-3" /> No projects match</div>
        ) : (
          filtered.map((p) => (
            <label key={p.id} data-testid={`project-picker-row-${p.id}`} className="flex items-center gap-2 px-1 py-1.5 text-[11px] cursor-pointer hover:bg-surface2">
              <input type={multiple ? "checkbox" : "radio"} checked={selectedIds.includes(p.id)} onChange={() => toggle(p.id)} className="rounded border-hairline" />
              <span className="truncate">{p.name}{p.client_name ? ` · ${p.client_name}` : ""}</span>
            </label>
          ))
        )}
      </div>
      <button onClick={submit} disabled={selectedIds.length === 0 || submitting} data-testid="project-picker-submit-btn"
        className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-md bg-ink px-2 py-1.5 text-[11px] text-white hover:bg-brand disabled:opacity-50">
        {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} {submitLabel}
      </button>
    </div>
  );
};
